// hooks
import { type MutationParams, useCommonMutation, useCommonQuery } from 'hooks';

// modules
import type {
  BaseApiResponse,
  CommonQueryOptions,
  ReqSystemList,
  ReqSystemSave,
  ReqSystemUpdate,
  ResultError,
  SystemCode
} from 'modules';

// apis
import { SystemSafetyAPI } from '../apis/system-safety.api';

// keys
import { systemSafetyKeys } from '../keys/system-safety.key';

// libraries
import { UseMutationResult } from '@tanstack/react-query';

export const systemSafetyAPI = new SystemSafetyAPI();

/** 안전교육 시스템 목록 조회 쿼리 훅 */
export const useGetSystemSafetyList = ({
  data,
  options
}: {
  data: ReqSystemList;
  options?: CommonQueryOptions<BaseApiResponse<unknown, SystemCode>, ResultError, SystemCode[]>;
}) => {
  const queryKeyFactory = (data: ReqSystemList) => systemSafetyKeys.QUERY_KEY_SYSTEM_SAFETY(data);

  const queryFn = async (data: ReqSystemList) => {
    return await systemSafetyAPI.getSystemList(data);
  };

  return useCommonQuery<
    ReqSystemList,
    BaseApiResponse<unknown, SystemCode>,
    ResultError,
    SystemCode[]
  >({
    data,
    options: {
      ...options,
      select: (data) => data.resultList
    },
    queryKeyFactory,
    queryFn
  });
};

/** 안전교육 시스템 코드 추가 쿼리 훅 */
export const useSaveSystemSafetyCode = (): UseMutationResult<
  BaseApiResponse<SystemCode>,
  ResultError,
  MutationParams<BaseApiResponse<SystemCode>, ReqSystemSave>,
  unknown
> => {
  return useCommonMutation<BaseApiResponse<SystemCode>, ResultError, ReqSystemSave>({
    mutationKey: systemSafetyKeys.QUERY_KEY_SYSTEM_SAFETY,
    mutationFn: async (data) => {
      return await systemSafetyAPI.saveSystemCode(data);
    }
  });
};

/** 안전교육 시스템 코드 업데이트 쿼리 훅 */
export const useUpdateSystemSafetyCode = (): UseMutationResult<
  BaseApiResponse<SystemCode>,
  ResultError,
  MutationParams<BaseApiResponse<SystemCode>, ReqSystemUpdate>,
  unknown
> => {
  return useCommonMutation<BaseApiResponse<SystemCode>, ResultError, ReqSystemUpdate>({
    mutationKey: systemSafetyKeys.QUERY_KEY_SYSTEM_SAFETY,
    mutationFn: async (data) => {
      return await systemSafetyAPI.updateSystemCode(data);
    }
  });
};

/** 안전교육 시스템 목록 범위 조회 쿼리 훅 */
export const useGetSystemSafetyListByRange = ({
  data,
  options
}: {
  data: { clsCdStart?: number; clsCdLast?: number };
  options?: CommonQueryOptions<BaseApiResponse<unknown, SystemCode>, ResultError, SystemCode>;
}) => {
  const queryKeyFactory = (data: { clsCdStart?: number; clsCdLast?: number }) =>
    systemSafetyKeys.QUERY_KEY_GET_SYSTEM_LIST_BY_RANGE(data);

  const queryFn = async (data: { clsCdStart?: number; clsCdLast?: number }) => {
    return await systemSafetyAPI.getSystemListByRange(data);
  };

  return useCommonQuery<
    { clsCdStart?: number; clsCdLast?: number },
    BaseApiResponse<unknown, SystemCode>,
    ResultError,
    SystemCode
  >({
    data,
    options,
    queryKeyFactory,
    queryFn
  });
};

/** 안전교육 시스템 코드 삭제 쿼리 훅 */
export const useDeleteSystemSafetyCode = (): UseMutationResult<
  BaseApiResponse<string>,
  ResultError,
  MutationParams<BaseApiResponse<string>, string>,
  unknown
> => {
  return useCommonMutation<BaseApiResponse<string>, ResultError, string>({
    mutationKey: systemSafetyKeys.QUERY_KEY_SYSTEM_SAFETY,
    mutationFn: async (data) => {
      return await systemSafetyAPI.deleteSystemCode(data);
    }
  });
};
